import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enAU } from 'date-fns/locale';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as ActivateIcon,
  Add as ExtendIcon,
  Gavel as AuctionIcon,
  ArrowBack,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import openScheduleService, { 
  type OpenSchedule, 
  type CreateOpenScheduleData,
  type ScheduleStats,
  type AuctionResultData,
  type OpenSession
} from '../services/openScheduleService';
import SessionManager from '../components/SessionManager';
import AdhocSessionDialog from '../components/AdhocSessionDialog';
import AvailabilitySuggestions from '../components/AvailabilitySuggestions';
import CalendarAssistantBar from '../components/CalendarAssistantBar';
import { getProperty } from '../services/propertyService';
import { getPropertyWorkflows } from '../services/workflowService';

// Helper function to get saleType from property
const getSaleType = (property?: any): 'sale' | 'auction' | 'rental' => {
  if (!property) return 'sale';
  
  // Use property.saleType if available (set during initial consultation)
  if (property.saleType) {
    if (property.saleType === 'auction') {
      return 'auction';
    }
    return 'sale';
  }
  
  // Fallback to deriving from workflows if saleType not set yet
  // This should only happen for older properties without saleType set
  return 'sale';
};

// Helper function to format a date as YYYY-MM-DD in local time (not UTC)
// This avoids timezone issues where toISOString() can shift the date by one day
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OpenSchedulePage: React.FC = () => {
  const { propertyId, agencySlug } = useParams<{ propertyId: string; agencySlug: string }>();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState<OpenSchedule | null>(null);
  const [stats, setStats] = useState<ScheduleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [property, setProperty] = useState<any>(null);
  const [consultationCompleted, setConsultationCompleted] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAuctionDialog, setOpenAuctionDialog] = useState(false);
  const [openAuctionScheduleDialog, setOpenAuctionScheduleDialog] = useState(false);
  const [openExtendDialog, setOpenExtendDialog] = useState(false);
  const [openSessionDialog, setOpenSessionDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState<OpenSession | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<OpenSchedule | null>(null);
  const [openAdhocDialog, setOpenAdhocDialog] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [manualSessions, setManualSessions] = useState<Array<{
    date: string;
    startTime: string;
    endTime: string;
  }>>([]);
  const [newSessionDraft, setNewSessionDraft] = useState<{
    date: string;
    startTime: string;
    endTime: string;
  }>({ date: '', startTime: '', endTime: '' });

  // Form state
  const [formData, setFormData] = useState<CreateOpenScheduleData>({
    propertyId: propertyId || '',
    totalWeeks: 4,
    startDate: '',
    endDate: '',
    weekDays: [3, 6], // Default to Wednesday and Saturday
    isFlexible: false,
    maxWeeks: 12,
    autoExtend: false,
    defaultStartTime: '10:00',
    defaultEndTime: '10:30',
    requireRegistration: false
  });
  const [activeWeekDay, setActiveWeekDay] = useState<number | null>(
    formData.weekDays[0] ?? null
  );

  // Track draft times for each day of the week (0-6)
  const [dayDrafts, setDayDrafts] = useState<Record<number, { startTime: string; endTime: string }>>({});

  // Whenever the active weekday changes:
  // 1. Save current draft times to the PREVIOUS day's draft store (if any)
  // 2. Load draft times for the NEW day (or default/clear)
  const [prevActiveDay, setPrevActiveDay] = useState<number | null>(null);

  useEffect(() => {
    // Save current draft to the previous day before switching context
    if (prevActiveDay !== null && prevActiveDay !== activeWeekDay) {
      setDayDrafts(prev => ({
        ...prev,
        [prevActiveDay]: {
          startTime: newSessionDraft.startTime,
          endTime: newSessionDraft.endTime
        }
      }));
    }
    
    setPrevActiveDay(activeWeekDay);
  }, [activeWeekDay, newSessionDraft.startTime, newSessionDraft.endTime]);

  useEffect(() => {
    if (activeWeekDay === null) return;
    const today = new Date();
    const currentDow = today.valueOf() ? today.getDay() : 0;
    const diff = (activeWeekDay - currentDow + 7) % 7;
    const target = new Date(today);
    target.setDate(today.getDate() + diff);
    const dateStr = formatLocalDate(target);
    
    // Load draft for this day if it exists, otherwise clear it
    const savedDraft = dayDrafts[activeWeekDay];
    
    setNewSessionDraft(prev => ({
      ...prev,
      date: dateStr,
      startTime: savedDraft?.startTime || '',
      endTime: savedDraft?.endTime || ''
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWeekDay]); // Only run when day changes, not when dayDrafts changes to avoid loops

  // Auction result form
  const [auctionData, setAuctionData] = useState<AuctionResultData>({
    result: 'sold',
    reservePrice: undefined
  });

  // Extension form
  const [extensionWeeks, setExtensionWeeks] = useState(2);

  useEffect(() => {
    if (propertyId) {
      loadData();
      loadPropertyAndWorkflows();
    }
  }, [propertyId]);

  const loadPropertyAndWorkflows = async () => {
    try {
      // Load property data
      const propertyResponse = await getProperty(agencySlug!, propertyId!);
      setProperty(propertyResponse);
      
      // Load workflows (for consultation status check)
      const workflowsResponse = await getPropertyWorkflows(propertyId!, agencySlug!);
      
      // Check if initial consultation is completed
      const consultationWorkflow = workflowsResponse.find((w: any) => 
        w.type === 'consultation' || w.name.toLowerCase().includes('consultation')
      );
      
      if (consultationWorkflow) {
        setConsultationCompleted(consultationWorkflow.status === 'completed');
      } else if (propertyResponse?.consultationStatus) {
        setConsultationCompleted(propertyResponse.consultationStatus === 'completed');
      } else if (propertyResponse?.stages?.length > 0) {
        const consultationStage = propertyResponse.stages.find((s: any) => 
          s.name.toLowerCase().includes('consultation')
        );
        setConsultationCompleted(consultationStage?.status === 'completed');
      } else {
        setConsultationCompleted(false);
      }
    } catch (err) {
      console.error('Error loading property/workflow data:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const scheduleData = await openScheduleService.getOpenSchedule(propertyId!, agencySlug!);
      
      // Ensure only future sessions are in the schedule (additional safety filter on frontend)
      if (scheduleData && scheduleData.sessions && Array.isArray(scheduleData.sessions)) {
        const now = new Date();
        const futureSessions = scheduleData.sessions.filter((session: any) => {
          // Include if status is in_progress (currently happening)
          if (session.status === 'in_progress') {
            return true;
          }
          // Only include scheduled sessions (not completed or cancelled)
          if (session.status !== 'scheduled') {
            return false;
          }
          // Check if session date/time is in the future
          if (!session.date) {
            return false;
          }
          const sessionDate = new Date(session.date);
          if (isNaN(sessionDate.getTime())) {
            return false;
          }
          // If we have a start time, create a proper date-time
          if (session.startTime) {
            try {
              const [hours, minutes] = session.startTime.split(':').map(Number);
              if (!isNaN(hours) && !isNaN(minutes)) {
                sessionDate.setHours(hours, minutes, 0, 0);
              } else {
                sessionDate.setHours(0, 0, 0, 0);
              }
            } catch (e) {
              sessionDate.setHours(0, 0, 0, 0);
            }
          } else {
            sessionDate.setHours(0, 0, 0, 0);
          }
          // Compare dates (accounting for milliseconds)
          return sessionDate.getTime() >= now.getTime();
        });
        
        // Create a new object to ensure React detects the change
        setSchedule({
          ...scheduleData,
          sessions: futureSessions
        });
      } else {
        setSchedule(scheduleData);
      }
      
      // Only get stats if schedule has an ID (not empty)
      if (scheduleData && scheduleData._id) {
        const statsData = await openScheduleService.getScheduleStats(scheduleData._id, agencySlug!);
        setStats(statsData);
      } else {
        // Set empty stats for empty schedule
        setStats({
          totalSessions: 0,
          registeredAttendees: 0,
          completedSessions: 0,
          upcomingSessions: 0
        });
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No schedule exists yet - this should not happen anymore with the backend change
        setSchedule(null);
      } else {
        setError(err.message || 'Failed to load open schedule data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    setEditingSchedule(null);
    setManualSessions([]);
    
    // Helper function to add minutes to time string (HH:MM format)
    const addMinutesToTime = (timeStr: string, minutes: number): string => {
      const [hours, mins] = timeStr.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, mins, 0, 0);
      date.setMinutes(date.getMinutes() + minutes);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    };

    // Use schedule's endDate for auction date, and defaultStartTime + 30 mins for auction time
    let auctionDate = schedule?.endDate || '';
    let auctionTime = '';
    
    // For auction time, use defaultEndTime + 30 mins (30 mins after opening end time)
    // Priority: schedule.defaultEndTime > formData.defaultEndTime > default '10:30'
    const endTime = schedule?.defaultEndTime || formData.defaultEndTime || '10:30';
    auctionTime = addMinutesToTime(endTime, 30);
    
    setFormData({
      propertyId: propertyId || '',
      totalWeeks: 4,
      startDate: '',
      endDate: '',
      weekDays: [3, 6], // Default to Wednesday and Saturday
      isFlexible: getSaleType(property) === 'sale',
      maxWeeks: 12,
      autoExtend: false,
      defaultStartTime: '10:00',
      defaultEndTime: '10:30',
      requireRegistration: false,
      auctionDate: auctionDate,
      auctionTime: auctionTime,
      auctionLocation: 'On Site'
    });
    setOpenDialog(true);
  };

  // Auto-open Create Open Schedule dialog if query param present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldOpen = params.get('create') === '1' || params.get('openDialog') === '1';
    if (shouldOpen) {
      handleCreateSchedule();
    }
  }, []);

  // Update auction date/time when endDate or sessions change
  useEffect(() => {
    if (openDialog && !editingSchedule && getSaleType(property) === 'auction') {
      // Helper function to add minutes to time string (HH:MM format)
      const addMinutesToTime = (timeStr: string, minutes: number): string => {
        const [hours, mins] = timeStr.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, mins, 0, 0);
        date.setMinutes(date.getMinutes() + minutes);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      };

      // Find the last session from manualSessions to determine auction time
      let lastSession = null;
      if (manualSessions.length > 0) {
        const sortedSessions = [...manualSessions].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB.getTime() - dateA.getTime(); // Latest date first
          }
          return b.endTime.localeCompare(a.endTime); // Latest time first
        });
        lastSession = sortedSessions[0];
      } else if (newSessionDraft.date && newSessionDraft.startTime) {
        // If no confirmed sessions, use the current draft as the reference
        lastSession = {
          date: newSessionDraft.date,
          startTime: newSessionDraft.startTime,
          endTime: newSessionDraft.endTime
        };
      }

      if (lastSession) {
        // Calculate the date based on total weeks
        // If totalWeeks is 4, we want the session date + 3 weeks
        const weeksToAdd = Math.max(0, (formData.totalWeeks || 1) - 1);
        const sessionDate = new Date(lastSession.date);
        sessionDate.setDate(sessionDate.getDate() + (weeksToAdd * 7));
        const finalAuctionDate = sessionDate.toISOString().split('T')[0];

        // If we have sessions, set auction 30 mins after the last session starts
        const newAuctionTime = addMinutesToTime(lastSession.startTime, 30);
        
        // Only update if changed
        if (formData.auctionDate !== finalAuctionDate || formData.auctionTime !== newAuctionTime) {
          setFormData(prev => ({
            ...prev,
            auctionDate: finalAuctionDate,
            auctionTime: newAuctionTime
          }));
        }
      } else {
        // Fallback to default logic if no sessions yet
        const shouldUpdate = !formData.auctionDate || formData.endDate;
        
        if (shouldUpdate && formData.endDate) {
          // Use defaultStartTime + 30 mins
          const startTime = schedule?.defaultStartTime || formData.defaultStartTime || '10:00';
          const auctionTime = addMinutesToTime(startTime, 30);
          
          setFormData(prev => ({
            ...prev,
            auctionDate: formData.endDate || prev.auctionDate,
            auctionTime: auctionTime || prev.auctionTime
          }));
        }
      }
    }
  }, [openDialog, editingSchedule, property, schedule, formData.endDate, formData.defaultEndTime, formData.auctionDate, formData.auctionTime, manualSessions, newSessionDraft, formData.totalWeeks]);

  const handleEditSchedule = () => {
    if (schedule) {
      setEditingSchedule(schedule);
      // Seed manual sessions from existing schedule so times can be edited day-by-day
      const seededSessions =
        (schedule.sessions || []).map(s => ({
          date: typeof s.date === 'string' ? s.date : new Date(s.date).toISOString().split('T')[0],
          startTime: s.startTime,
          endTime: s.endTime
        }));
      setManualSessions(seededSessions);
      setFormData({
        propertyId: schedule.propertyId,
        totalWeeks: schedule.totalWeeks,
        startDate: schedule.startDate,
        endDate: schedule.endDate,
                 weekDays: schedule.weekDays || [3, 6], // Default to Wednesday and Saturday if not set
        isFlexible: schedule.isFlexible,
        maxWeeks: schedule.maxWeeks,
        autoExtend: schedule.autoExtend,
        defaultStartTime: schedule.defaultStartTime,
        defaultEndTime: schedule.defaultEndTime,
        auctionDate: schedule.auctionDate,
        auctionTime: schedule.auctionTime,
        auctionLocation: schedule.auctionLocation,
        reservePrice: schedule.reservePrice,
        notes: schedule.notes,
        maxAttendeesPerSession: schedule.maxAttendeesPerSession,
        requireRegistration: schedule.requireRegistration
      });
      setOpenDialog(true);
    }
  };

  const handleOpenAuctionSchedule = () => {
    // Helper function to add minutes to time string (HH:MM format)
    const addMinutesToTime = (timeStr: string, minutes: number): string => {
      const [hours, mins] = timeStr.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, mins, 0, 0);
      date.setMinutes(date.getMinutes() + minutes);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    };

    // Find the last open session (sorted by date and time)
    let lastSession: OpenSession | null = null;
    if (schedule && schedule.sessions && schedule.sessions.length > 0) {
      const sortedSessions = [...schedule.sessions].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime(); // Latest first
        }
        return b.startTime.localeCompare(a.startTime); // Latest time first
      });
      lastSession = sortedSessions[0];
    }

    // Set defaults based on last session or existing auction data
    let defaultAuctionDate = schedule?.auctionDate || '';
    let defaultAuctionTime = schedule?.auctionTime || '';
    let defaultAuctionLocation = schedule?.auctionLocation || 'on site';

    // If no existing auction data but we have a last session, use that as default
    if (!schedule?.auctionDate && lastSession) {
      defaultAuctionDate = lastSession.date;
      defaultAuctionTime = addMinutesToTime(lastSession.endTime, 30);
      defaultAuctionLocation = 'on site';
    }

    // Load existing auction data or use defaults
    setFormData({
      ...formData,
      auctionDate: defaultAuctionDate,
      auctionTime: defaultAuctionTime,
      auctionLocation: defaultAuctionLocation,
      reservePrice: schedule?.reservePrice,
    });
    setOpenAuctionScheduleDialog(true);
  };

  const handleAuctionScheduleSubmit = async () => {
    try {
      // Validate auction fields
      if (!formData.auctionDate) {
        setError('Auction Date is required');
        return;
      }
      if (!formData.auctionTime) {
        setError('Auction Time is required');
        return;
      }
      
      // If schedule exists, update it with auction details
      if (schedule && schedule._id) {
        await openScheduleService.updateOpenSchedule(schedule._id, {
          // Preserve other schedule fields
          propertyId: schedule.propertyId,
          totalWeeks: schedule.totalWeeks,
          startDate: schedule.startDate,
          endDate: schedule.endDate,
          weekDays: schedule.weekDays,
          isFlexible: schedule.isFlexible,
          maxWeeks: schedule.maxWeeks,
          autoExtend: schedule.autoExtend,
          defaultStartTime: schedule.defaultStartTime,
          defaultEndTime: schedule.defaultEndTime,
          notes: schedule.notes,
          maxAttendeesPerSession: schedule.maxAttendeesPerSession,
          requireRegistration: schedule.requireRegistration,
          // Auction fields - explicitly include them
          auctionDate: formData.auctionDate,
          auctionTime: formData.auctionTime,
          auctionLocation: formData.auctionLocation,
          reservePrice: formData.reservePrice
        }, agencySlug!);
      } else {
        // Create new schedule with auction details
        await openScheduleService.createOpenSchedule({
          ...formData,
          propertyId: propertyId || '',
          totalWeeks: 1,
          startDate: formData.auctionDate || '',
          endDate: formData.auctionDate || '',
          weekDays: [],
          isFlexible: false,
          maxWeeks: 8,
          autoExtend: false,
          defaultStartTime: formData.auctionTime || '10:00',
          defaultEndTime: formData.auctionTime || '10:30',
        }, agencySlug!);
      }
      setOpenAuctionScheduleDialog(false);
      loadData();
    } catch (err: any) {
      const rawMessage = err?.response?.data?.message || err?.message || 'Failed to save auction schedule';
      let friendly = rawMessage;

      // Friendlier copy for overlap / buffer issues
      if (rawMessage.toLowerCase().includes('conflict') || rawMessage.toLowerCase().includes('buffer')) {
        friendly = 'This auction time clashes with another event for one of the assigned agents, or doesn’t leave a 30 minute gap. Please pick a different time so all agents have enough travel and setup time between openings.';
      }

      setError(friendly);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate auction fields for auction properties
      if (getSaleType(property) === 'auction') {
        if (!formData.auctionDate) {
          setError('Auction Date is required for auction properties');
          return;
        }
        if (!formData.auctionTime) {
          setError('Auction Time is required for auction properties');
          return;
        }
      }
      
      const payload: any = {
        ...formData
      };

      if (manualSessions.length > 0) {
        payload.sessions = manualSessions;
        // Derive weekDays from the manual sessions so backend metadata matches
        const derivedWeekDays = Array.from(
          new Set(
            manualSessions.map(s => new Date(s.date).getDay())
          )
        ).sort();
        payload.weekDays = derivedWeekDays;
      }

      if (editingSchedule) {
        await openScheduleService.updateOpenSchedule(editingSchedule._id, payload, agencySlug!);
      } else {
        await openScheduleService.createOpenSchedule(payload, agencySlug!);
      }
      setOpenDialog(false);
      setError(null); // Clear any previous errors
      await loadData();
    } catch (err: any) {
      console.error('Error creating/updating schedule:', err);
      const rawMessage = err?.response?.data?.message || err?.message || 'Failed to save schedule';
      let friendly = rawMessage;

      // Friendlier copy for overlap / buffer issues
      if (rawMessage.toLowerCase().includes('conflict') || rawMessage.toLowerCase().includes('buffer')) {
        friendly = 'This schedule would overlap another event for one of the assigned agents, or it doesn’t leave a 30 minute gap between openings. Please adjust the days or times so all assignees are free with at least 30 minutes between appointments.';
      }

      setError(friendly);
    }
  };

  const handleActivate = async () => {
    if (schedule) {
      try {
        await openScheduleService.activateSchedule(schedule._id, agencySlug!);
        loadData();
      } catch (err: any) {
        setError(err.message || 'Failed to activate schedule');
      }
    }
  };

  const handleExtend = async () => {
    if (schedule) {
      try {
        await openScheduleService.extendSchedule(schedule._id, agencySlug!, extensionWeeks);
        setOpenExtendDialog(false);
        loadData();
      } catch (err: any) {
        setError(err.message || 'Failed to extend schedule');
      }
    }
  };

  const handleAuctionResult = async () => {
    if (schedule) {
      try {
        await openScheduleService.handleAuctionResult(schedule._id, agencySlug!, auctionData);
        setOpenAuctionDialog(false);
        loadData();
      } catch (err: any) {
        setError(err.message || 'Failed to record auction result');
      }
    }
  };

     

  const handleSessionClick = (session: OpenSession) => {
    // Prevent editing past sessions
    const now = new Date();
    const sessionDate = new Date(session.date);
    if (session.startTime) {
      const [hours, minutes] = session.startTime.split(':').map(Number);
      sessionDate.setHours(hours || 0, minutes || 0, 0, 0);
    }
    const isPast = sessionDate < now && session.status !== 'in_progress';
    
    if (isPast) {
      // Don't allow editing past sessions
      return;
    }
    // If it's an auction session, open the auction scheduling dialog
    if (session.isAuction) {
      handleOpenAuctionSchedule();
    } else {
      setSelectedSession(session);
      setOpenSessionDialog(true);
    }
  };

  const handleSessionUpdate = (updatedSession: OpenSession) => {
    if (schedule) {
      const updatedSessions = schedule.sessions.map(s => 
        s._id === updatedSession._id ? updatedSession : s
      );
      setSchedule({ ...schedule, sessions: updatedSessions });
    }
  };

  const handleAdhocSessionAdded = (newSession: OpenSession) => {
    if (schedule) {
      const updatedSessions = [...schedule.sessions, newSession];
      setSchedule({ ...schedule, sessions: updatedSessions });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Check if consultation is completed
  if (!consultationCompleted) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Initial Consultation Required
          </Typography>
          <Typography variant="body2">
            Open house scheduling is only available after the initial consultation workflow is completed. 
            Please complete the consultation process first.
          </Typography>
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBack />}
          onClick={() => {
            // Navigate back and let PropertyDetails restore scroll position
            navigate(`/agencies/${agencySlug}/properties/${propertyId}`, {
              state: { restoreScroll: true }
            });
          }}
        >
          Back to Property
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              // Navigate back and let PropertyDetails restore scroll position
              navigate(`/agencies/${agencySlug}/properties/${propertyId}`, {
                state: { restoreScroll: true }
              });
            }}
            variant="outlined"
          >
            Back to Property
          </Button>
          <Typography variant="h4" component="h1">
            Open House Schedule
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!schedule || !schedule._id ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            No Active Open Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            There is no active open house schedule for this property. Create a new schedule to start managing property viewings.
            {getSaleType(property) === 'auction' && ' For auction properties, you can also schedule the auction date, time, and location.'}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSchedule}
            >
              Create Open Schedule
            </Button>
            {getSaleType(property) === 'auction' && (
              <Button
                variant="contained"
                startIcon={<AuctionIcon />}
                onClick={handleOpenAuctionSchedule}
                color="error"
              >
                Schedule Auction
              </Button>
            )}
          </Box>
        </Paper>
      ) : schedule && schedule._id ? (
        <Grid container spacing={3}>
          {/* Schedule Overview */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Schedule Overview</Typography>
                <Box>
                  {/* Use property's saleType if available, otherwise fallback to schedule's saleType */}
                  {(() => {
                    const displaySaleType = getSaleType(property) || schedule.saleType || 'sale';
                    return (
                      <Chip 
                        label={displaySaleType.replace('_', ' ').toUpperCase()} 
                        color={displaySaleType === 'auction' ? 'error' : 'primary'}
                        sx={{ mr: 1 }}
                      />
                    );
                  })()}
                  <Chip 
                    label={schedule.status ? schedule.status.toUpperCase() : 'DRAFT'} 
                    color={schedule.status === 'active' ? 'success' : 'default'}
                  />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Total Weeks</Typography>
                  <Typography variant="h6">{schedule.totalWeeks}</Typography>
                </Grid>
                                 <Grid item xs={6} md={3}>
                   <Typography variant="body2" color="text.secondary">Openings/Week</Typography>
                   <Typography variant="h6">{schedule.weekDays.length}</Typography>
                 </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Start Date</Typography>
                  <Typography variant="h6">{openScheduleService.formatDate(schedule.startDate)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">End Date</Typography>
                  <Typography variant="h6">{openScheduleService.formatDate(schedule.endDate)}</Typography>
                </Grid>
              </Grid>

              {getSaleType(property) === 'auction' && (() => {
                // Only show auction date if it's in the future
                const now = new Date();
                if (schedule.auctionDate && schedule.auctionTime) {
                  const auctionDateObj = new Date(schedule.auctionDate);
                  const [hours, minutes] = schedule.auctionTime.split(':').map(Number);
                  auctionDateObj.setHours(hours || 0, minutes || 0, 0, 0);
                  if (auctionDateObj < now) {
                    // Auction is in the past, don't show it
                    return null;
                  }
                }
                return (
                  <Box 
                    mt={2} 
                    p={2} 
                    sx={{ 
                      bgcolor: 'primary.main', // Blue background for better readability
                      borderRadius: 1,
                      color: 'white',
                      boxShadow: 1
                    }}
                  >
                    {schedule.auctionDate ? (
                      <>
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                          🏠 Auction: {openScheduleService.formatDate(schedule.auctionDate)} at {schedule.auctionTime}
                        </Typography>
                        {schedule.auctionLocation && (
                          <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                            Location: {schedule.auctionLocation}
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AuctionIcon />}
                          onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/auction-scheduling`)}
                          sx={{ 
                            mt: 1,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.3)',
                            }
                          }}
                        >
                          Manage Auction Scheduling
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }} gutterBottom>
                          🏠 Auction Property - Schedule Required
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                          Please schedule the auction date, time, and location.
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AuctionIcon />}
                          onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/auction-scheduling`)}
                          color="error"
                        >
                          Schedule Auction
                        </Button>
                      </>
                    )}
                  </Box>
                );
              })()}

              {schedule.isFlexible && schedule.extensionCount > 0 && (
                <Box mt={2} p={2} bgcolor="warning.light" borderRadius={1}>
                  <Typography variant="subtitle2" color="warning.dark">
                    ⏰ Extended {schedule.extensionCount} time(s) - Last extended: {schedule.lastExtendedDate ? openScheduleService.formatDate(schedule.lastExtendedDate) : 'N/A'}
                  </Typography>
                </Box>
              )}

              <Box mt={3} display="flex" gap={1} flexWrap="wrap">
                {schedule.status === 'draft' && (
                  <Button
                    variant="contained"
                    startIcon={<ActivateIcon />}
                    onClick={handleActivate}
                  >
                    Activate
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditSchedule}
                >
                  Edit
                </Button>
                {getSaleType(property) === 'auction' && (
                  <Button
                    variant="outlined"
                    startIcon={<AuctionIcon />}
                    onClick={handleOpenAuctionSchedule}
                    color="error"
                  >
                    {schedule?.auctionDate ? 'Edit Auction Schedule' : 'Schedule Auction'}
                  </Button>
                )}
                {openScheduleService.canExtend(schedule) && (
                  <Button
                    variant="outlined"
                    startIcon={<ExtendIcon />}
                    onClick={() => setOpenExtendDialog(true)}
                  >
                    Extend
                  </Button>
                )}
                {openScheduleService.canRecordAuctionResult(schedule) && (
                  <Button
                    variant="outlined"
                    startIcon={<AuctionIcon />}
                    onClick={() => setOpenAuctionDialog(true)}
                  >
                    Record Auction Result
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Statistics */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" mb={2}>Statistics</Typography>
              {stats && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
                    <Typography variant="h6">{stats.totalSessions}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Completed</Typography>
                    <Typography variant="h6">{stats.completedSessions}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Upcoming</Typography>
                    <Typography variant="h6">{stats.upcomingSessions}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Total Attendees</Typography>
                    <Typography variant="h6">{stats.totalAttendees}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Avg Attendees</Typography>
                    <Typography variant="h6">{stats.averageAttendees}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Extensions</Typography>
                    <Typography variant="h6">{stats.extensionCount}</Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* Sessions Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Sessions</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAdhocDialog(true)}
                  sx={{ ml: 2 }}
                >
                  Add One-Time Session
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Week</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Attendees</TableCell>
                      <TableCell>Notes</TableCell>
                                              <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schedule.sessions
                        .filter((session) => {
                          // Show only sessions that haven't happened yet
                          const now = new Date();
                          
                          // Handle in_progress sessions
                          if (session.status === 'in_progress') {
                            return true;
                          }
                          
                          // Skip completed or cancelled sessions
                          if (session.status === 'completed' || session.status === 'cancelled') {
                            return false;
                          }
                          
                          // Create session date-time from date and startTime
                          if (!session.date) {
                            return false;
                          }
                          
                          const sessionDate = new Date(session.date);
                          if (isNaN(sessionDate.getTime())) {
                            return false;
                          }
                          
                          // Parse start time if available
                          if (session.startTime) {
                            try {
                              const [hours, minutes] = session.startTime.split(':').map(Number);
                              if (!isNaN(hours) && !isNaN(minutes)) {
                                sessionDate.setHours(hours, minutes, 0, 0);
                              }
                            } catch (e) {
                              // If time parsing fails, default to start of day
                              sessionDate.setHours(0, 0, 0, 0);
                            }
                          } else {
                            // Default to start of day if no time specified
                            sessionDate.setHours(0, 0, 0, 0);
                          }
                          
                          // Include if session is in the future
                          return sessionDate >= now;
                        })
                        .sort((a, b) => {
                          // First sort by date
                          const dateA = new Date(a.date);
                          const dateB = new Date(b.date);
                          if (dateA.getTime() !== dateB.getTime()) {
                            return dateA.getTime() - dateB.getTime();
                          }
                          // If same date, sort by start time
                          return a.startTime.localeCompare(b.startTime);
                        })
                        .map((session) => (
                        <TableRow 
                          key={session._id}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => handleSessionClick(session)}
                        >
                          <TableCell>
                            {session.isAuction ? (
                              <Chip label="Auction" size="small" color="error" variant="outlined" icon={<AuctionIcon />} />
                            ) : session.isAdhoc ? (
                              <Chip label="Ad-hoc" size="small" color="primary" variant="outlined" />
                            ) : (
                              session.weekNumber
                            )}
                          </TableCell>
                          <TableCell>{openScheduleService.formatDate(session.date)}</TableCell>
                          <TableCell>
                            {openScheduleService.formatTime(session.startTime)} - {openScheduleService.formatTime(session.endTime)}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={session.status.replace('_', ' ').toUpperCase()} 
                              size="small"
                              sx={{ 
                                bgcolor: openScheduleService.getSessionStatusColor(session.status),
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {session.currentAttendees}
                            {session.maxAttendees && ` / ${session.maxAttendees}`}
                          </TableCell>
                          <TableCell>{session.notes || '-'}</TableCell>
                          <TableCell>
                            {(() => {
                              // Disable edit for past sessions
                              const now = new Date();
                              const sessionDate = new Date(session.date);
                              if (session.startTime) {
                                const [hours, minutes] = session.startTime.split(':').map(Number);
                                sessionDate.setHours(hours || 0, minutes || 0, 0, 0);
                              }
                              const isPast = sessionDate < now && session.status !== 'in_progress';
                              
                              return (
                                <IconButton 
                                  size="small" 
                                  disabled={isPast}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isPast) {
                                      handleSessionClick(session);
                                    }
                                  }}
                                  title={isPast ? 'Cannot edit past sessions' : 'Edit session'}
                                >
                                  <EditIcon />
                                </IconButton>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      {/* Create/Edit Schedule Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingSchedule ? 'Edit Open Schedule' : 'Create Open Schedule'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>




                         <Grid item xs={12}>
               <TextField
                 label="Total Weeks"
                 type="number"
                 value={formData.totalWeeks}
                 onChange={(e) => {
                   const newTotalWeeks = parseInt(e.target.value);
                   let newEndDate = formData.endDate;
                   
                   // Recalculate end date if start date is set
                   if (formData.startDate && newTotalWeeks !== formData.totalWeeks) {
                     const start = new Date(formData.startDate);
                     const end = new Date(start);
                     end.setDate(end.getDate() + (newTotalWeeks * 7) - 1);
                     newEndDate = end.toISOString().split('T')[0];
                   }
                   
                   setFormData({ 
                     ...formData, 
                     totalWeeks: newTotalWeeks,
                     endDate: newEndDate
                   });
                 }}
                                   fullWidth
                  inputProps={{ min: 1, max: getSaleType(property) === 'auction' ? 8 : 24 }}
               />
             </Grid>

             <Grid item xs={12}>
               <Typography variant="subtitle2" gutterBottom>
                 Days of the Week for Openings
               </Typography>
               <Box display="flex" gap={1} flexWrap="wrap">
                 {[
                   { value: 0, label: 'Sun' },
                   { value: 1, label: 'Mon' },
                   { value: 2, label: 'Tue' },
                   { value: 3, label: 'Wed' },
                   { value: 4, label: 'Thu' },
                   { value: 5, label: 'Fri' },
                   { value: 6, label: 'Sat' }
                 ].map((day) => {
                   const isSelected = formData.weekDays.includes(day.value);
                   const isActive = activeWeekDay === day.value;
                   return (
                     <Chip
                       key={day.value}
                       label={day.label}
                       onClick={() => {
                         if (isSelected) {
                           // Already selected: just switch view to this day. Keep existing schedules for other days.
                           setActiveWeekDay(day.value);
                           return;
                         }
                         // Not selected: add day to weekDays and switch view
                         setFormData({ ...formData, weekDays: [...formData.weekDays, day.value] });
                         setActiveWeekDay(day.value);
                       }}
                       color={isActive ? 'primary' : isSelected ? 'default' : 'default'}
                       variant={isActive ? 'filled' : isSelected ? 'outlined' : 'outlined'}
                       sx={{
                         cursor: 'pointer',
                         borderWidth: isActive ? 2 : 1
                       }}
                     />
                   );
                 })}
               </Box>
                               <Typography variant="caption" color="text.secondary">
                  Select which days of the week to have openings
                </Typography>
             </Grid>

            {/* Explicit openings (per-day time slots) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Openings for{' '}
                {activeWeekDay !== null
                  ? ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][activeWeekDay]
                  : 'All Days'}
              </Typography>
              {manualSessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No specific openings selected yet. Choose a day above, then add individual
                  time slots for that day, or use the Schedule Assistant.
                </Typography>
              ) : (
                <TableContainer sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manualSessions
                        .filter(s =>
                          activeWeekDay === null
                            ? true
                            : new Date(s.date).getDay() === activeWeekDay
                        )
                        .map((s, index) => (
                          <TableRow key={`${s.date}-${s.startTime}-${s.endTime}-${index}`}>
                            <TableCell>
                              {openScheduleService.formatDate(s.date)}
                            </TableCell>
                            <TableCell>
                              {openScheduleService.formatTime(s.startTime)} –{' '}
                              {openScheduleService.formatTime(s.endTime)}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const toRemove = { date: s.date, startTime: s.startTime, endTime: s.endTime };
                                  setManualSessions(prev => {
                                    const idx = prev.findIndex(
                                      row =>
                                        row.date === toRemove.date &&
                                        row.startTime === toRemove.startTime &&
                                        row.endTime === toRemove.endTime
                                    );
                                    if (idx === -1) return prev;
                                    return prev.filter((_, i) => i !== idx);
                                  });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>

            {/* Add opening manually */}
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Opening Date"
                value={newSessionDraft.date ? new Date(newSessionDraft.date) : null}
                onChange={(date: Date | null) => {
                  setNewSessionDraft(draft => ({
                    ...draft,
                    date: date ? date.toISOString().split('T')[0] : ''
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { shrink: true }
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Opening Start Time"
                type="time"
                value={newSessionDraft.startTime}
                onChange={(e) =>
                  setNewSessionDraft(draft => ({ ...draft, startTime: e.target.value }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Opening End Time"
                type="time"
                value={newSessionDraft.endTime}
                onChange={(e) =>
                  setNewSessionDraft(draft => ({ ...draft, endTime: e.target.value }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (!newSessionDraft.date) {
                    setError('Please select a date for the opening.');
                    return;
                  }

                  // If time not specified, reuse the last selected opening time if available
                  let startTime = newSessionDraft.startTime;
                  let endTime = newSessionDraft.endTime;

                  if ((!startTime || !endTime) && manualSessions.length > 0) {
                    const last = manualSessions[manualSessions.length - 1];
                    startTime = startTime || last.startTime;
                    endTime = endTime || last.endTime;
                  }

                  if (!startTime || !endTime) {
                    setError('Please provide a start and end time, or add at least one opening to copy from.');
                    return;
                  }

                  const finalSession = {
                    date: newSessionDraft.date,
                    startTime,
                    endTime
                  };

                  setManualSessions(prev => {
                    const exists = prev.some(
                      s =>
                        s.date === finalSession.date &&
                        s.startTime === finalSession.startTime &&
                        s.endTime === finalSession.endTime
                    );
                    if (exists) {
                      return prev;
                    }
                    return [...prev, finalSession];
                  });

                  // Align schedule dates/weekdays with the new manual session
                  setFormData(prev => {
                    const dates = [
                      ...manualSessions.map(s => s.date),
                      finalSession.date
                    ].filter(Boolean);
                    let startDate = prev.startDate;
                    let endDate = prev.endDate;
                    if (dates.length > 0) {
                      const dObjs = dates.map(d => new Date(d));
                      const min = new Date(Math.min(...dObjs.map(d => d.getTime())));
                      startDate = min.toISOString().split('T')[0];
                      
                      // Calculate end date based on total weeks from start date
                      const weeks = prev.totalWeeks || 4;
                      const end = new Date(min);
                      end.setDate(end.getDate() + (weeks * 7) - 1);
                      
                      // Use the later of the two: calculated end date or the last manual session
                      // We must ensure the end date covers the total weeks specified
                      const maxSessionDate = new Date(Math.max(...dObjs.map(d => d.getTime())));
                      endDate = (end > maxSessionDate ? end : maxSessionDate).toISOString().split('T')[0];
                    }

                    const pickedDay = new Date(finalSession.date).getDay();
                    const weekDays = Array.isArray(prev.weekDays) ? [...prev.weekDays] : [];
                    if (!weekDays.includes(pickedDay)) {
                      weekDays.push(pickedDay);
                    }

                    return {
                      ...prev,
                      startDate,
                      endDate,
                      weekDays
                    };
                  });

                  // Reset draft and clear saved draft for this day
                  setNewSessionDraft({ date: '', startTime: '', endTime: '' });
                  if (activeWeekDay !== null) {
                    setDayDrafts(prev => ({
                      ...prev,
                      [activeWeekDay]: { startTime: '', endTime: '' }
                    }));
                  }
                }}
              >
                Add Opening
              </Button>

              {/* Open calendar assistant from within the dialog */}
              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={() => setAssistantOpen(true)}
              >
                Find Time with Assistant
              </Button>

              {/* Inline Calendar Assistant (only when requested) */}
              {assistantOpen && propertyId && agencySlug && (
                <Box sx={{ mt: 2 }}>
                  <CalendarAssistantBar
                    open={assistantOpen}
                    onClose={() => setAssistantOpen(false)}
                    propertyId={propertyId}
                    initialDate={(() => {
                      // If a weekday is active, jump to the next occurrence of that weekday
                      if (activeWeekDay !== null) {
                        const today = new Date();
                        const currentDow = today.getDay();
                        const diff =
                          (activeWeekDay - currentDow + 7) % 7;
                        const target = new Date(today);
                        target.setDate(today.getDate() + diff);
                        return formatLocalDate(target);
                      }
                      return undefined;
                    })()}
                    currentSelection={
                      newSessionDraft.startTime && newSessionDraft.endTime
                        ? {
                            date: newSessionDraft.date,
                            startTime: newSessionDraft.startTime,
                            endTime: newSessionDraft.endTime
                          }
                        : undefined
                    }
                    localSessions={manualSessions}
                    agencySlug={agencySlug}
                    onSuggestionSelect={(suggestion) => {
                      const pickedDate = suggestion.date;
                      const pickedDay = new Date(pickedDate).getDay();

                      const newSession = {
                        date: pickedDate,
                        startTime: suggestion.startTime,
                        endTime: suggestion.endTime
                      };

                      // Update the draft opening fields so the user sees what was picked
                      setNewSessionDraft(prev => ({
                        ...prev,
                        date: pickedDate,
                        startTime: suggestion.startTime,
                        endTime: suggestion.endTime
                      }));

                      setManualSessions(prev => {
                        const exists = prev.some(
                          s =>
                            s.date === newSession.date &&
                            s.startTime === newSession.startTime &&
                            s.endTime === newSession.endTime
                        );
                        const next = exists ? prev : [...prev, newSession];

                        // Align schedule metadata with all selected sessions
                        setFormData(prevForm => {
                          const dates = next.map(s => s.date);
                          let startDate = prevForm.startDate;
                          let endDate = prevForm.endDate;
                          if (dates.length > 0) {
                            const dObjs = dates.map(d => new Date(d));
                            const min = new Date(Math.min(...dObjs.map(d => d.getTime())));
                            startDate = min.toISOString().split('T')[0];
                            
                            // Calculate end date based on total weeks from start date
                            const weeks = prevForm.totalWeeks || 4;
                            const end = new Date(min);
                            end.setDate(end.getDate() + (weeks * 7) - 1);
                            
                            // Use the later of the two: calculated end date or the last manual session
                            const maxSessionDate = new Date(Math.max(...dObjs.map(d => d.getTime())));
                            endDate = (end > maxSessionDate ? end : maxSessionDate).toISOString().split('T')[0];
                          }

                          const weekDays = Array.isArray(prevForm.weekDays)
                            ? [...prevForm.weekDays]
                            : [];
                          if (!weekDays.includes(pickedDay)) {
                            weekDays.push(pickedDay);
                          }

                          return {
                            ...prevForm,
                            startDate,
                            endDate,
                            weekDays
                          };
                        });

                        return next;
                      });
                    }}
                    onWarningMessage={(message) => {
                      setError(message);
                    }}
                  />
                </Box>
              )}
            </Grid>

                         {getSaleType(property) === 'sale' && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isFlexible}
                        onChange={(e) => setFormData({ ...formData, isFlexible: e.target.checked })}
                      />
                    }
                    label="Flexible Schedule (can be extended)"
                  />
                </Grid>

                {formData.isFlexible && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        label="Maximum Weeks"
                        type="number"
                        value={formData.maxWeeks}
                        onChange={(e) => setFormData({ ...formData, maxWeeks: parseInt(e.target.value) })}
                        fullWidth
                        inputProps={{ min: 1, max: 52 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.autoExtend}
                            onChange={(e) => setFormData({ ...formData, autoExtend: e.target.checked })}
                          />
                        }
                        label="Auto-extend if no sale"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

                         {getSaleType(property) === 'auction' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
                    Auction Details (Required)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Auction Date"
                    value={formData.auctionDate ? new Date(formData.auctionDate) : null}
                    onChange={(date: Date | null) => {
                      const auctionDate = date ? date.toISOString().split('T')[0] : '';
                      setFormData({ ...formData, auctionDate });
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        InputLabelProps: { shrink: true },
                        onFocus: async () => {
                          // Refetch auction date/time when field is focused
                          // Helper function to add minutes to time string (HH:MM format)
                          const addMinutesToTime = (timeStr: string, minutes: number): string => {
                            const [hours, mins] = timeStr.split(':').map(Number);
                            const date = new Date();
                            date.setHours(hours, mins, 0, 0);
                            date.setMinutes(date.getMinutes() + minutes);
                            return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                          };

                          // Use endDate for auction date
                          let auctionDate = formData.endDate || schedule?.endDate || '';
                          
                          // For auction time, use defaultStartTime + 30 mins (30 mins after opening start time)
                          // Priority: schedule.defaultStartTime > formData.defaultStartTime > default '10:00'
                          const startTime = schedule?.defaultStartTime || formData.defaultStartTime || '10:00';
                          const auctionTime = addMinutesToTime(startTime, 30);
                          
                          // Only update if we have values
                          if (auctionDate || auctionTime) {
                            setFormData(prev => ({
                              ...prev,
                              auctionDate: auctionDate || prev.auctionDate,
                              auctionTime: auctionTime || prev.auctionTime,
                              auctionLocation: prev.auctionLocation || 'On Site'
                            }));
                          }
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Auction Time"
                    type="time"
                    value={formData.auctionTime || ''}
                    onChange={(e) => setFormData({ ...formData, auctionTime: e.target.value })}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Auction Location"
                    value={formData.auctionLocation || ''}
                    onChange={(e) => setFormData({ ...formData, auctionLocation: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Reserve Price"
                    type="number"
                    value={formData.reservePrice || ''}
                    onChange={(e) => setFormData({ ...formData, reservePrice: parseFloat(e.target.value) || undefined })}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      </LocalizationProvider>

      {/* Auction Schedule Dialog - Simplified dialog for one-time auction session */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
        <Dialog open={openAuctionScheduleDialog} onClose={() => setOpenAuctionScheduleDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{schedule?.auctionDate ? 'Edit Auction Schedule' : 'Schedule Auction'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <DatePicker
                  label="Auction Date *"
                  value={formData.auctionDate ? new Date(formData.auctionDate) : null}
                  onChange={(date: Date | null) => {
                    setFormData({ ...formData, auctionDate: date ? date.toISOString().split('T')[0] : '' });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputLabelProps: { shrink: true }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Auction Time *"
                  type="time"
                  value={formData.auctionTime || ''}
                  onChange={(e) => setFormData({ ...formData, auctionTime: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }} // 5 minute increments
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Auction Location"
                  value={formData.auctionLocation || ''}
                  onChange={(e) => setFormData({ ...formData, auctionLocation: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="e.g., On-site at property address"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reserve Price"
                  type="number"
                  value={formData.reservePrice || ''}
                  onChange={(e) => setFormData({ ...formData, reservePrice: parseFloat(e.target.value) || undefined })}
                  fullWidth
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                  placeholder="Optional"
                />
              </Grid>
            </Grid>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenAuctionScheduleDialog(false);
              setError(null);
            }}>Cancel</Button>
            <Button onClick={handleAuctionScheduleSubmit} variant="contained" color="error">
              {schedule?.auctionDate ? 'Update Auction Schedule' : 'Schedule Auction'}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      {/* Extend Schedule Dialog */}
      <Dialog open={openExtendDialog} onClose={() => setOpenExtendDialog(false)}>
        <DialogTitle>Extend Schedule</DialogTitle>
        <DialogContent>
          <TextField
            label="Additional Weeks"
            type="number"
            value={extensionWeeks}
            onChange={(e) => setExtensionWeeks(parseInt(e.target.value))}
            fullWidth
            sx={{ mt: 1 }}
            inputProps={{ min: 1, max: 12 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExtendDialog(false)}>Cancel</Button>
          <Button onClick={handleExtend} variant="contained">
            Extend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auction Result Dialog */}
      <Dialog open={openAuctionDialog} onClose={() => setOpenAuctionDialog(false)}>
        <DialogTitle>Record Auction Result</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Auction Result</InputLabel>
                <Select
                  value={auctionData.result}
                  onChange={(e) => setAuctionData({ ...auctionData, result: e.target.value as any })}
                  label="Auction Result"
                >
                  <MenuItem value="sold">Sold</MenuItem>
                  <MenuItem value="passed_in">Passed In</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reserve Price"
                type="number"
                value={auctionData.reservePrice || ''}
                onChange={(e) => setAuctionData({ ...auctionData, reservePrice: parseFloat(e.target.value) || undefined })}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAuctionDialog(false)}>Cancel</Button>
          <Button onClick={handleAuctionResult} variant="contained">
            Record Result
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Manager Dialog */}
      <SessionManager
        open={openSessionDialog}
        onClose={() => setOpenSessionDialog(false)}
        session={selectedSession}
        scheduleId={schedule?._id || ''}
        agencySlug={agencySlug!}
        onSessionUpdate={handleSessionUpdate}
      />

      {/* Ad-hoc Session Dialog */}
      <AdhocSessionDialog
        open={openAdhocDialog}
        onClose={() => setOpenAdhocDialog(false)}
        onSuccess={handleAdhocSessionAdded}
        scheduleId={schedule?._id || ''}
        agencySlug={agencySlug!}
        propertyId={propertyId}
        propertyAddress={property ? `${property.address?.street}, ${property.address?.city}` : undefined}
        existingSessions={schedule?.sessions || []}
      />

    </Box>
  );
};

export default OpenSchedulePage; 